import PhoenixOffcanvas from 'components/base/PhoenixOffcanvas';
import Section from 'components/base/Section';
import { useEffect, useState } from 'react';
import { Button, Col, Pagination, Row, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Scrollbar from 'components/base/Scrollbar';
import ProductFilterItems, {
  ProductFilter
} from 'components/modules/e-commerce/products-filter/ProductFilterItems';
import ProductCard from 'components/common/ProductCard';
import {
  faChevronLeft,
  faChevronRight,
  faFilter
} from '@fortawesome/free-solid-svg-icons';
import { getProductsByFiltering } from 'api/products/_requests';
import { PF } from 'helpers/utils';
import { useSelector } from 'react-redux';
import { RootState } from 'redux-state/store';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Brand,
  Category,
  CategoryType,
  getAllBrands,
  getAllCategoriesByCategoryType,
  getAllCategoryTypes,
  getAllSubCategoriesByCategory,
  SubCategory
} from 'api/categories/_requests';
import { queryClient } from 'index';
import { useLocation } from 'react-router-dom';

interface Product {
  id: string;
  image: string;
  slug: string;
  name: string;
  rating: number;
  rated?: number;
  price?: number;
  salePrice?: number;
  colors?: number;
  extra?: string;
  extraClass?: string;
  extra2?: string;
  extra2Class?: string;
  verified?: boolean;
  wishListed?: boolean;
  offer?: string;
  dealEndTime?: string;
  wishlistedVariantId?: string | null;
}

const ProductsFilter = () => {
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const { wishlist } = useSelector((state: RootState) => state.user.wishlist);
  const { user } = useSelector((state: RootState) => state.auth);
  const { loading: wishlistLoading } = useSelector(
    (state: RootState) => state.user.wishlist
  );
  const location = useLocation();
  const state = location.state || {};
  const searchValue = location.state?.search || '';
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);

  const [filters, setFilters] = useState<ProductFilter>({
    brands: [],
    categoryType: null,
    category: null,
    subCategory: [],
    price: {
      min: null,
      max: null
    },
    search: searchValue
  });

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      search: searchValue,
      category: state.category || null,
      categoryType: state.categoryType || null
    }));
  }, [searchValue, state.category, state.categoryType]);

  const { data: brands } = useQuery<Brand[]>({
    queryKey: ['all-brands'],
    queryFn: () => getAllBrands()
  });

  const { data: categoryTypes } = useQuery<CategoryType[]>({
    queryKey: ['all-category-types'],
    queryFn: () => getAllCategoryTypes()
  });

  const { data: categories } = useQuery<Category[]>({
    queryKey: ['all-categories', filters.categoryType],
    queryFn: () =>
      filters.categoryType
        ? getAllCategoriesByCategoryType(filters.categoryType)
        : Promise.resolve([])
  });

  const { data: subCategories } = useQuery<SubCategory[]>({
    queryKey: ['all-sub-categories', filters.category],
    queryFn: () =>
      filters.category
        ? getAllSubCategoriesByCategory(filters.category)
        : Promise.resolve([])
  });

  const { data, isLoading, isError } = useQuery<{
    products: Product[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
  }>({
    queryKey: ['products', filters, page, limit],
    queryFn: async () => {
      const response = await getProductsByFiltering(filters, limit, page);
      console.log(':res', response);

      return {
        products: response.products.map(
          (item: {
            _id: string;
            slug: string;
            thumbnail: string | undefined;
            name: string;
            price: number;
            discountedPrice: number;
            vendorId: string;
            variantIds: string[];
          }) => {
            const wishlisted = wishlist.find(
              wishlistItem =>
                wishlistItem.productId === item._id &&
                item.variantIds.includes(wishlistItem.variantId || '')
            );

            return {
              id: item._id,
              image:
                PF +
                (item?.thumbnail === undefined ? 'goods' : item?.thumbnail),
              name: item.name,
              slug: item.slug,
              price: item.discountedPrice ? item.price : undefined,
              salePrice: item.discountedPrice
                ? item.discountedPrice
                : item.price,
              wishListed: !!wishlisted,
              wishlistedVariantId: wishlisted?.variantId || item.variantIds[0],
              vendorId: item.vendorId,
              rating: 0 // ✅ required by Product interface
            };
          }
        ),
        totalCount: response.totalCount,
        totalPages: response.totalPages,
        currentPage: response.currentPage
      };
    },
    enabled: user?.role === 'customer' ? !wishlistLoading : true
  });

  const totalCount = data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / limit);
  const products = data?.products || [];

  const { mutate: setProduct } = useMutation({
    mutationFn: (params: {
      index: number;
      wishListed: boolean;
      wishlistedVariantId: string | null;
    }) => {
      const temp = [...products];
      temp[params.index].wishListed = params.wishListed;
      temp[params.index].wishlistedVariantId = params.wishlistedVariantId;
      return Promise.resolve(temp);
    },
    onSuccess: newProducts => {
      queryClient.setQueryData(['products', filters], newProducts);
    }
  });

  const chunk = <T,>(arr: T[], size: number): T[][] =>
    Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
      arr.slice(i * size, i * size + size)
    );

  return (
    <div>
      <PhoenixOffcanvas
        open={show}
        onHide={handleClose}
        style={{ width: 300, top: 92 }}
        className="py-5 ps-5"
        fixed
      >
        <Scrollbar className="table-scrollbar">
          <div className="pe-5">
            <ProductFilterItems
              handleClose={handleClose}
              categoryTypes={categoryTypes ?? []}
              brands={brands ?? []}
              categories={categories ?? []}
              subCategories={subCategories ?? []}
              filters={filters}
              setFilters={setFilters}
            />
          </div>
        </Scrollbar>
      </PhoenixOffcanvas>
      <Section className="pt-5 pb-9">
        <Button
          variant="phoenix-secondary"
          size="sm"
          className="text-body-tertiary mb-5 d-lg-none"
          onClick={handleShow}
        >
          <FontAwesomeIcon icon={faFilter} className="me-2" />
          Filter
        </Button>
        <Row>
          <Col lg={3} xxl={2} className="d-none d-lg-block ps-xl-0 ps-xxl-3">
            <div
              className="position-sticky"
              style={{ top: '1rem', height: 'calc(100vh - 2rem) ' }}
            >
              <Scrollbar className="product-scrollbar">
                <ProductFilterItems
                  handleClose={handleClose}
                  brands={brands ?? []}
                  categoryTypes={categoryTypes ?? []}
                  categories={categories ?? []}
                  subCategories={subCategories ?? []}
                  filters={filters}
                  setFilters={setFilters}
                />
              </Scrollbar>
            </div>
          </Col>
          <Col lg={9} xxl={10}>
            {isLoading ? (
              <div className="d-flex justify-content-center h-100 align-items-center">
                <Spinner animation="grow" variant="primary" />
              </div>
            ) : (
              chunk(products, 5).map((rowProducts, rowIdx) => (
                <Row
                  key={rowIdx}
                  className="mb-4 border border-secondary-subtle rounded p-2 gap-0"
                >
                  {rowProducts.map((product, index) => (
                    <Col key={product.id}>
                      <div className="product-card-container h-100">
                        <ProductCard
                          product={product}
                          setProduct={() =>
                            setProduct({
                              index: rowIdx * 5 + index,
                              wishListed: !product.wishListed,
                              wishlistedVariantId:
                                product.wishlistedVariantId || null
                            })
                          }
                        />
                      </div>
                    </Col>
                  ))}
                </Row>
              ))
            )}
            <Pagination className="mb-0 justify-content-end">
              <Pagination.Prev
                onClick={() => setPage(p => Math.max(p - 1, 1))}
                disabled={page === 1}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Pagination.Prev>

              {/* First Page */}
              <Pagination.Item active={page === 1} onClick={() => setPage(1)}>
                1
              </Pagination.Item>
              <Pagination.Item active={page === 2} onClick={() => setPage(2)}>
                2
              </Pagination.Item>
              {/* Ellipsis if gap between first and current */}
              {page > 2 && <Pagination.Ellipsis disabled />}

              {/* Pages before and after current */}
              {page > 2 && page < totalPages - 1 && (
                <>
                  <Pagination.Item onClick={() => setPage(page - 1)}>
                    {page - 1}
                  </Pagination.Item>
                  <Pagination.Item active>{page}</Pagination.Item>
                  <Pagination.Item onClick={() => setPage(page + 1)}>
                    {page + 1}
                  </Pagination.Item>
                </>
              )}

              {page < totalPages - 2 && <Pagination.Ellipsis disabled />}

              {totalPages > 1 && (
                <Pagination.Item
                  active={page === totalPages}
                  onClick={() => setPage(totalPages)}
                >
                  {totalPages}
                </Pagination.Item>
              )}

              <Pagination.Next
                onClick={() => setPage(p => Math.min(p + 1, totalPages))}
                disabled={page === totalPages}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Pagination.Next>
            </Pagination>
          </Col>
        </Row>
      </Section>
    </div>
  );
};

export default ProductsFilter;
